import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { current } from 'immer';
import { Class, TournamentState } from '../../types/tournament';
import {
    IFreeSlotItem,
    IMatchScheduleItem,
    IScheduleConstraints,
    IScheduleDayConstraints,
    isFreeSlot,
    ITournamentSchedule,
} from '../../types/schedule';
import { FilterItem } from '../../pages/tourDetails/Schedule/components/schedule.helpers';
import { clearDisplayScheduleViewCache } from '../../pages/tourDetails/Schedule/components/scheduleDisplayView';
import {
    cloneScheduleSnapshot,
    MAX_SCHEDULE_HISTORY,
    type ScheduleHistorySnapshot,
} from './scheduleHistory';
import { MAX_SCHEDULE_BULK_SELECTION } from './scheduleSelectionConstants';

type State = {
    tournamentInfo: TournamentState | null;
    classIdToObject: Record<string, Class>;
    scheduleConstraints: IScheduleConstraints | null;
    scheduleConstraintsGiven: boolean;
    schedule: ITournamentSchedule | null;
    unscheduledMatches: IMatchScheduleItem[];
    displaySchedule?: ITournamentSchedule;
    selectedItem: IMatchScheduleItem | IFreeSlotItem | null;
    currentFilters: FilterItem[];
    message: string;
    error: string;
    isLoading: boolean;
    isCreatingSchedule: boolean;
    updatingSchedule: boolean;
    publishingSchedule: boolean;
    hasUnpublishedChanges: boolean;
    isFreeEditMode: boolean;
    loadedSavedSchedule: boolean;
    scheduleItemScaleMultiplier: number;
    scheduleColumnWidth: number;
    isGeneratingAsync: boolean;
    jobQueuePosition: number;
    jobEtaSeconds: number;
    /** When true (schedule editor only), tournament tag navigation is hidden for more workspace */
    scheduleEditorFullscreen: boolean;
    /** Local-only undo/redo for manual schedule edits */
    scheduleUndoStack: ScheduleHistorySnapshot[];
    scheduleRedoStack: ScheduleHistorySnapshot[];
    /** Multi-select for bulk adjust, group drag, bulk delete (max 50) */
    selectedMatchIds: number[];
    /** Session-only timestamps for toolbar status chip (ISO strings) */
    scheduleLastDraftSavedAt: string | null;
    scheduleLastPublishedAt: string | null;
    /** Set when Play tab saves match data; cleared after schedule refetch */
    scheduleStaleFromPlay: boolean;
};

/** Snapshot live Redux fields for undo/redo stacks — unwrap Immer drafts once, then one structuredClone in cloneScheduleSnapshot. */
const snapshotLiveScheduleForHistory = (state: State): ScheduleHistorySnapshot =>
    cloneScheduleSnapshot({
        schedule: state.schedule === null ? null : current(state.schedule),
        unscheduledMatches: current(state.unscheduledMatches),
    });

const initialState: State = {
    tournamentInfo: null,
    classIdToObject: {},
    scheduleConstraints: null,
    scheduleConstraintsGiven: false,
    schedule: null,
    unscheduledMatches: [],
    selectedItem: null,
    currentFilters: [],
    message: '',
    error: '',
    isLoading: false,
    isCreatingSchedule: false,
    updatingSchedule: false,
    publishingSchedule: false,
    hasUnpublishedChanges: false,
    isFreeEditMode: false,
    loadedSavedSchedule: false,
    scheduleItemScaleMultiplier: 10,
    scheduleColumnWidth: 290,
    isGeneratingAsync: false,
    jobQueuePosition: 0,
    jobEtaSeconds: 0,
    scheduleEditorFullscreen: false,
    scheduleUndoStack: [],
    scheduleRedoStack: [],
    selectedMatchIds: [],
    scheduleLastDraftSavedAt: null,
    scheduleLastPublishedAt: null,
    scheduleStaleFromPlay: false,
};

export const scheduleSlice = createSlice({
    name: 'schedule',
    initialState,
    reducers: {
        initialDataFetching: (state) => {
            state.isLoading = true;
        },

        initialDataFetchSuccess: (state) => {
            state.isLoading = false;
            state.scheduleStaleFromPlay = false;
        },

        initialDataFetchError: (state, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        setTournamentInfo: (state, action: PayloadAction<TournamentState>) => {
            state.tournamentInfo = action.payload;

            state.classIdToObject = state.tournamentInfo!.classes.reduce(
                (accumulator, currentClass) => {
                    accumulator[currentClass.id] = currentClass;
                    return accumulator;
                },
                {} as Record<string, Class>
            );
        },

        setLoadedSavedSchedule: (state) => {
            state.loadedSavedSchedule = true;
        },

        creatingSchedule: (state) => {
            state.isCreatingSchedule = true;
        },

        creatingScheduleSuccess: (state) => {
            state.isCreatingSchedule = false;
        },

        creatingScheduleError: (state, action: PayloadAction<string>) => {
            state.isCreatingSchedule = false;
            state.error = action.payload;
        },

        setGeneratingAsync: (state, action: PayloadAction<boolean>) => {
            state.isGeneratingAsync = action.payload;
        },

        setScheduleConstraints: (state, action: PayloadAction<IScheduleConstraints>) => {
            state.scheduleConstraints = action.payload;
        },

        updateScheduleConstraintDays: (state, action: PayloadAction<IScheduleDayConstraints[]>) => {
            if (state.scheduleConstraints !== null) {
                state.scheduleConstraints.days = action.payload;
                state.scheduleConstraintsGiven = true;
            }
        },

        updateAllScheduleConstraints: (state, action: PayloadAction<IScheduleConstraints>) => {
            state.scheduleConstraints = action.payload;
            state.scheduleConstraintsGiven = true;
        },

        setScheduleConstraintsGiven: (state, action: PayloadAction<boolean>) => {
            state.scheduleConstraintsGiven = action.payload;
        },

        /** This overrides the schedule state and should only be used when changing the schedule locally (manually) */
        setScheduleState: (state, action: PayloadAction<ITournamentSchedule>) => {
            state.schedule = action.payload;
        },

        markScheduleStaleFromPlay: (state) => {
            state.scheduleStaleFromPlay = true;
        },

        /** This should be called when inserting the data from Backend. We detect if it has unpublished changes. */
        setScheduleStateFromSource: (state, action: PayloadAction<ITournamentSchedule>) => {
            clearDisplayScheduleViewCache();
            state.schedule = action.payload;
            state.scheduleUndoStack = [];
            state.scheduleRedoStack = [];
            const allScheduleMatches = action.payload?.days.flatMap((day) =>
                day.courts.flatMap((court) => court.matches)
            );
            const hasUnpublishedSchedule = allScheduleMatches?.some(
                (match) => !isFreeSlot(match) && match.startDate !== match.scheduleDate
            );
            state.hasUnpublishedChanges = hasUnpublishedSchedule;
            if (!hasUnpublishedSchedule) {
                state.isFreeEditMode = false;
            }
        },

        setUnscheduledMatchesState: (state, action: PayloadAction<IMatchScheduleItem[]>) => {
            state.unscheduledMatches = action.payload;
        },

        applyScheduleAndUnscheduled: (
            state,
            action: PayloadAction<{ schedule: ITournamentSchedule; unscheduledMatches: IMatchScheduleItem[] }>
        ) => {
            state.schedule = action.payload.schedule;
            state.unscheduledMatches = action.payload.unscheduledMatches;
        },

        pushScheduleHistoryBeforeEdit: (state, action: PayloadAction<ScheduleHistorySnapshot>) => {
            state.scheduleRedoStack = [];
            state.scheduleUndoStack.push(action.payload);
            while (state.scheduleUndoStack.length > MAX_SCHEDULE_HISTORY) {
                state.scheduleUndoStack.shift();
            }
        },

        undoScheduleChange: (state) => {
            if (state.scheduleUndoStack.length === 0) return;
            /** Already deep-cloned when pushed — do not clone again. */
            const previous = state.scheduleUndoStack.pop()!;
            state.scheduleRedoStack.push(snapshotLiveScheduleForHistory(state));
            state.schedule = previous.schedule;
            state.unscheduledMatches = previous.unscheduledMatches;
        },

        redoScheduleChange: (state) => {
            if (state.scheduleRedoStack.length === 0) return;
            /** Already deep-cloned when pushed — do not clone again. */
            const next = state.scheduleRedoStack.pop()!;
            state.scheduleUndoStack.push(snapshotLiveScheduleForHistory(state));
            while (state.scheduleUndoStack.length > MAX_SCHEDULE_HISTORY) {
                state.scheduleUndoStack.shift();
            }
            state.schedule = next.schedule;
            state.unscheduledMatches = next.unscheduledMatches;
        },

        clearScheduleHistory: (state) => {
            state.scheduleUndoStack = [];
            state.scheduleRedoStack = [];
        },

        /** Drop last undo snapshot after reverting local state (e.g. failed API update). */
        discardLastScheduleUndoSnapshot: (state) => {
            if (state.scheduleUndoStack.length > 0) {
                state.scheduleUndoStack.pop();
            }
        },

        setDisplayScheduleState: (state, action: PayloadAction<ITournamentSchedule>) => {
            state.displaySchedule = action.payload;
        },

        setSelectedItem: (
            state,
            action: PayloadAction<IMatchScheduleItem | IFreeSlotItem | null>
        ) => {
            state.selectedItem = action.payload;
        },

        setScheduleSelection: (state, action: PayloadAction<number[]>) => {
            state.selectedMatchIds = action.payload.slice(0, MAX_SCHEDULE_BULK_SELECTION);
        },

        toggleScheduleMatchSelected: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            const idx = state.selectedMatchIds.indexOf(id);
            if (idx >= 0) {
                state.selectedMatchIds.splice(idx, 1);
            } else if (state.selectedMatchIds.length < MAX_SCHEDULE_BULK_SELECTION) {
                state.selectedMatchIds.push(id);
            }
        },

        addScheduleMatchesToSelection: (state, action: PayloadAction<number[]>) => {
            const merged = new Set([...state.selectedMatchIds, ...action.payload]);
            state.selectedMatchIds = [...merged].slice(0, MAX_SCHEDULE_BULK_SELECTION);
        },

        clearScheduleSelection: (state) => {
            state.selectedMatchIds = [];
        },

        setCurrentFilters: (state, action: PayloadAction<FilterItem[]>) => {
            state.currentFilters = action.payload;
        },

        updateSchedule: (state) => {
            state.updatingSchedule = true;
        },

        updateScheduleSuccess: (state) => {
            state.updatingSchedule = false;
            state.hasUnpublishedChanges = true;
            state.scheduleLastDraftSavedAt = new Date().toISOString();
            state.error = '';
            state.message = 'toast-scheduleUpdated';
        },

        updateScheduleError: (state, action: PayloadAction<string>) => {
            state.updatingSchedule = false;
            state.error = action.payload;
            state.message = '';
        },

        publishSchedule: (state) => {
            state.publishingSchedule = true;
        },

        publishScheduleSuccess: (state) => {
            state.publishingSchedule = false;
            state.hasUnpublishedChanges = false;
            state.isFreeEditMode = false;
            state.scheduleLastPublishedAt = new Date().toISOString();
            state.error = '';
            state.message = 'toast-schedulePublished';
        },

        publishScheduleError: (state, action: PayloadAction<string>) => {
            state.publishingSchedule = false;
            state.error = action.payload;
        },

        scheduleClearNotification: (state) => {
            state.error = '';
            state.message = '';
        },

        setFreeEditMode: (state, action: PayloadAction<boolean>) => {
            state.isFreeEditMode = action.payload;
        },

        setScheduleItemScaleMultiplier: (state, action: PayloadAction<number>) => {
            state.scheduleItemScaleMultiplier = action.payload;
        },
        setScheduleColumnWidth: (state, action: PayloadAction<number>) => {
            state.scheduleColumnWidth = action.payload;
        },

        setScheduleEditorFullscreen: (state, action: PayloadAction<boolean>) => {
            state.scheduleEditorFullscreen = action.payload;
        },

        clearScheduleStates: (state) => {
            const matchesOnGrid = state.schedule?.days.flatMap(day =>
                day.courts.flatMap(court =>
                    court.matches.filter(match => !isFreeSlot(match))
                )
            ) || [];

            state.scheduleConstraintsGiven = false;
            state.schedule = null;

            state.unscheduledMatches = [...state.unscheduledMatches, ...matchesOnGrid] as IMatchScheduleItem[];

            state.selectedItem = null;
            state.selectedMatchIds = [];
            state.currentFilters = [];
            state.isFreeEditMode = false;
            state.scheduleItemScaleMultiplier = initialState.scheduleItemScaleMultiplier;
            state.scheduleEditorFullscreen = false;
            state.scheduleUndoStack = [];
            state.scheduleRedoStack = [];
            state.scheduleLastDraftSavedAt = null;
            state.scheduleLastPublishedAt = null;
        },

        setJobProgress: (state, action: PayloadAction<{ queuePosition: number; etaSeconds: number }>) => {
            state.jobQueuePosition = action.payload.queuePosition;
            state.jobEtaSeconds = action.payload.etaSeconds;
        },

        restoreInitialState: (state) => {
            clearDisplayScheduleViewCache();
            state.tournamentInfo = initialState.tournamentInfo;
            state.classIdToObject = initialState.classIdToObject;
            state.scheduleConstraints = initialState.scheduleConstraints;
            state.scheduleConstraintsGiven = initialState.scheduleConstraintsGiven;
            state.schedule = initialState.schedule;
            state.unscheduledMatches = initialState.unscheduledMatches;
            state.selectedItem = initialState.selectedItem;
            state.selectedMatchIds = initialState.selectedMatchIds;
            state.currentFilters = initialState.currentFilters;
            state.message = initialState.message;
            state.error = initialState.error;
            state.isLoading = initialState.isLoading;
            state.isCreatingSchedule = initialState.isCreatingSchedule;
            state.updatingSchedule = initialState.updatingSchedule;
            state.isFreeEditMode = initialState.isFreeEditMode;
            state.scheduleItemScaleMultiplier = initialState.scheduleItemScaleMultiplier;
            state.loadedSavedSchedule = false;
            state.isGeneratingAsync = initialState.isGeneratingAsync;
            state.displaySchedule = undefined;
            state.scheduleEditorFullscreen = false;
            state.scheduleUndoStack = [];
            state.scheduleRedoStack = [];
            state.scheduleLastDraftSavedAt = initialState.scheduleLastDraftSavedAt;
            state.scheduleLastPublishedAt = initialState.scheduleLastPublishedAt;
            state.scheduleStaleFromPlay = initialState.scheduleStaleFromPlay;
        },
    },
});

export const {
    initialDataFetching,
    initialDataFetchSuccess,
    initialDataFetchError,
    setTournamentInfo,
    setLoadedSavedSchedule,
    creatingSchedule,
    creatingScheduleSuccess,
    creatingScheduleError,
    setGeneratingAsync,
    setScheduleConstraints,
    updateScheduleConstraintDays,
    updateAllScheduleConstraints,
    setScheduleConstraintsGiven,
    setScheduleState,
    setScheduleStateFromSource,
    setUnscheduledMatchesState,
    applyScheduleAndUnscheduled,
    pushScheduleHistoryBeforeEdit,
    undoScheduleChange,
    redoScheduleChange,
    clearScheduleHistory,
    discardLastScheduleUndoSnapshot,
    setDisplayScheduleState,
    setSelectedItem,
    setScheduleSelection,
    toggleScheduleMatchSelected,
    addScheduleMatchesToSelection,
    clearScheduleSelection,
    setCurrentFilters,
    updateSchedule,
    updateScheduleSuccess,
    updateScheduleError,
    publishSchedule,
    publishScheduleSuccess,
    publishScheduleError,
    scheduleClearNotification,
    setFreeEditMode,
    setScheduleItemScaleMultiplier,
    setScheduleColumnWidth,
    setScheduleEditorFullscreen,
    clearScheduleStates,
    setJobProgress,
    restoreInitialState,
    markScheduleStaleFromPlay,
} = scheduleSlice.actions;

export default scheduleSlice.reducer;
